import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Chip, Divider } from '@common/material'
import {
  ArticleOutlined,
  AssignmentOutlined,
  FactCheckOutlined,
  FlagOutlined,
} from '@mui/icons-material'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import { localizedStrings } from '@core/strings'
import { statuses } from '@core/theme/statuses'
import { ActivePermitStatusChips } from '@modules/registers/propertyValue/RegisterItemPropertyPermitValue'
import { ActiveChecklistStatusChips } from '@modules/registers/propertyValue/RegisterItemPropertyChecklistValue'
import { ActiveTaskStatusChips } from '@modules/registers/propertyValue/RegisterItemPropertyTaskValue'
import { RegisterItemDetailsModal } from './RegisterItemDetailsModal'
import { useModal } from '@common/GlobalModal'
import { useNavigationContext } from '@common/hooks'

type RegisterItemStatusSummaryProps = {
  registerItem: RegisterItem
}

type StatusSummaryRowProps = {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}

const StatusSummaryRow: React.FC<StatusSummaryRowProps> = ({
  icon,
  label,
  children,
}) => (
  <Flex alignItems='center' sx={{ gap: 3 }}>
    <Flex alignItems='center' sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
      {icon}
      <Text
        color='text.secondary'
        sx={{
          fontSize: 14,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
    </Flex>
    <Flex alignItems='center' sx={{ flex: 3, minWidth: 0 }}>
      {children}
    </Flex>
  </Flex>
)

const iconSx = { fontSize: 16, color: 'text.secondary' }

export const RegisterItemStatusSummary: React.FC<
  RegisterItemStatusSummaryProps
> = ({ registerItem }) => {
  const { showModal } = useModal()
  const { organisationId } = useNavigationContext()
  const permitTotalsSum = registerItem.getPermitTotalsSum()
  const activeInspectionsCount = registerItem.getActiveInspectionsCount()
  const checklistTotalsSum = registerItem.getChecklistTotalsSum()
  const taskTotalsSum = registerItem.getTaskTotalsSum()
  const overdueInspectionsCount = registerItem.getOverdueInspectionsCount()

  if (!registerItem.haveStatusSummary()) {
    return null
  }

  const onChipClick = (tab: 'permit' | 'inspection' | 'checklist' | 'task') => {
    // during answer flow, register is undefined
    if (!registerItem.register) {
      return
    }
    showModal(
      <RegisterItemDetailsModal
        initialTabColumnId={tab}
        registerId={registerItem.register.id}
        registerItemId={registerItem.id}
        scope={{
          type: 'ORGANISATION',
          organisationId,
        }}
      />,
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Divider sx={{ mb: 0.5 }} />
      {permitTotalsSum > 0 && (
        <StatusSummaryRow
          icon={<ArticleOutlined sx={iconSx} />}
          label={localizedStrings.permits}
        >
          <ActivePermitStatusChips
            onPermitClick={() => onChipClick('permit')}
            totalPermits={registerItem.getPermitTotalsSum()}
            statusCountArray={registerItem.getActivePermitsStatusCountArray()}
          />
        </StatusSummaryRow>
      )}
      {activeInspectionsCount > 0 && (
        <StatusSummaryRow
          icon={<FlagOutlined sx={iconSx} />}
          label={localizedStrings.inspections}
        >
          <Box display='flex' flexWrap='wrap' gap={1}>
            <Chip
              variant='outlined'
              size='small'
              label={registerItem.getActiveInspectionsCount()}
              onClick={() => onChipClick('inspection')}
            />
            {overdueInspectionsCount > 0 && (
              <Chip
                color={statuses.overdue.color}
                size='small'
                label={`${statuses.overdue.label} (${overdueInspectionsCount})`}
                onClick={() => onChipClick('inspection')}
              />
            )}
          </Box>
        </StatusSummaryRow>
      )}
      {checklistTotalsSum > 0 && (
        <StatusSummaryRow
          icon={<FactCheckOutlined sx={iconSx} />}
          label={localizedStrings.checklists}
        >
          <ActiveChecklistStatusChips
            onChecklistClick={() => onChipClick('checklist')}
            totalChecklists={registerItem.getChecklistTotalsSum()}
            statusCountArray={registerItem.getChecklistsStatusCountArray()}
          />
        </StatusSummaryRow>
      )}
      {taskTotalsSum > 0 && (
        <StatusSummaryRow
          icon={<AssignmentOutlined sx={iconSx} />}
          label={localizedStrings.tasks}
        >
          <ActiveTaskStatusChips
            onTaskClick={() => onChipClick('task')}
            totalTasks={registerItem.getTaskTotalsSum()}
            statusCountArray={registerItem.getActiveTasksStatusCountArray()}
          />
        </StatusSummaryRow>
      )}
    </Box>
  )
}
